import {
  addMonths,
  endOfMonth,
  endOfQuarter,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  subMonths,
} from 'date-fns';

import type { Granularity, SelectedDateRange } from './types';

export const MONTH_COUNT = 12;
export const QUARTER_COUNT = 4;

/**
 * The longest period the KPI endpoints accept (`MAX_ALL_TIME_MONTHS` on the API, which
 * answers 400 to anything longer). Kept in step by hand: it is not in the OpenAPI schema.
 */
export const MAX_RANGE_MONTHS = 120;

/**
 * Same test the API applies: `start` is too early when it falls before `end` minus
 * `MAX_RANGE_MONTHS`. A range of exactly that length is allowed.
 */
export function exceedsMaxSpan(start: Date, end: Date): boolean {
  return startOfDay(start) < subMonths(startOfDay(end), MAX_RANGE_MONTHS);
}

/**
 * `start`, pulled forward to the earliest date the API accepts for `end` if it is earlier.
 * Snapped to the first of a month, so the Period chip's month label is what is requested
 * rather than a month whose first days are missing.
 */
export function clampRangeStart(start: Date, end: Date): Date {
  if (!exceedsMaxSpan(start, end)) {
    return start;
  }
  const floor = subMonths(startOfDay(end), MAX_RANGE_MONTHS);
  return floor.getDate() === 1 ? floor : startOfMonth(addMonths(floor, 1));
}

/** One clickable grid cell: a specific month or quarter of a specific year. */
export type PeriodUnit = {
  granularity: Granularity;
  year: number;
  /** 0-11 for a month; 0-3 for a quarter. */
  index: number;
};

export function resolveUnitRange(unit: PeriodUnit): { start: Date; end: Date } {
  if (unit.granularity === 'month') {
    const anchor = new Date(unit.year, unit.index, 1);
    return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
  }

  const anchor = new Date(unit.year, unit.index * 3, 1);
  return { start: startOfQuarter(anchor), end: endOfQuarter(anchor) };
}

/**
 * True only when `range` is exactly one whole unit of `granularity` — its
 * start and end sit precisely on that unit's boundaries, not merely within
 * it. A single month draft (Jun 1 - Jun 30) falls inside one quarter, but
 * isn't a whole quarter, so this must reject it under 'quarter': a mismatch
 * possible via Reset (which restores `appliedRange` without touching
 * `granularity`) or a `granularities` order where `handleOpen`'s default tab
 * doesn't match the applied value's own unit.
 */
function isSingleUnitRange(range: SelectedDateRange, granularity: Granularity): boolean {
  if (!range.start || !range.end) {
    return false;
  }

  const [start, end] =
    granularity === 'month'
      ? [startOfMonth(range.start), endOfMonth(range.start)]
      : [startOfQuarter(range.start), endOfQuarter(range.start)];

  return range.start.getTime() === start.getTime() && range.end.getTime() === end.getTime();
}

/**
 * One click completes a single unit. A second click — in either chronological
 * direction — extends it to a span; the earlier of the two becomes `start`.
 * A third click starts over, since by then the draft is a multi-unit span
 * rather than the single unit a second click extends from.
 */
export function reducePeriodClick(draft: SelectedDateRange, unit: PeriodUnit): SelectedDateRange {
  const clicked = resolveUnitRange(unit);

  if (!draft.start || !draft.end || !isSingleUnitRange(draft, unit.granularity)) {
    return clicked;
  }

  return clicked.start < draft.start
    ? { start: clicked.start, end: draft.end }
    : { start: draft.start, end: clicked.end };
}

/**
 * True when clicking `unit` would extend `draft` past `MAX_RANGE_MONTHS`. Only a
 * single-unit draft is extended by the next click (see `reducePeriodClick`); any other
 * draft is replaced by it, so no unit is out of span.
 */
export function isUnitBeyondMaxSpan(unit: PeriodUnit, draft: SelectedDateRange): boolean {
  if (!draft.start || !draft.end || !isSingleUnitRange(draft, unit.granularity)) {
    return false;
  }

  const clicked = resolveUnitRange(unit);
  const start = clicked.start < draft.start ? clicked.start : draft.start;
  const end = clicked.end > draft.end ? clicked.end : draft.end;
  return exceedsMaxSpan(start, end);
}

export function isUnitDisabled(
  unit: PeriodUnit,
  maxDate: Date,
  draft?: SelectedDateRange,
): boolean {
  return (
    resolveUnitRange(unit).start > maxDate || (draft ? isUnitBeyondMaxSpan(unit, draft) : false)
  );
}
